import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, Typography, Tabs, Tab, Button } from "@mui/material";
import Navbar from "../components/navbar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

const Section = ({ title, children }) => (
  <Card style={{ padding: 16, boxShadow: 2, borderRadius: 12 }}>
    <Typography variant="h6" style={{ marginBottom: 10, color: "#4180B7" }}>
      {title}
    </Typography>
    {children}
  </Card>
);

function ClientHub() {
  // File upload states for Job Packs & Donor Info
  const [jobPackFile, setJobPackFile] = useState(null);
  const [donorInfoFile, setDonorInfoFile] = useState(null);
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const clientEmailQuery = searchParams.get("email") || "";

  const [tab, setTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(clientId || "");
  const [jobRequests, setJobRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    primaryEmail: "",
    laboratoryAddress: "",
    secondBreathTestRequired: "No",
    drugKitType: "",
    nonNegativeSamplesToLab: "No",
    samplesBackToLabMethod: "",
    cocForm: false,
    resultsSent: false,
  });

  const selectedClient = useMemo(
    () => clients.find((c) => c._id === selectedClientId) || null,
    [clients, selectedClientId]
  );

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/getclients`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setClients(list);

        if (clientEmailQuery && !selectedClientId) {
          const matched = list.find((c) => c.emails?.includes(clientEmailQuery));
          if (matched) setSelectedClientId(matched._id);
        }
        if (!clientEmailQuery && !selectedClientId && list[0]) {
          setSelectedClientId(list[0]._id);
        }
      } catch (e) {
        console.error("Failed to fetch clients", e);
      }
    };

    const fetchJobs = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/getjobrequests?status=all`);
        const data = await res.json();
        setJobRequests(Array.isArray(data.data) ? data.data : []);
      } catch (e) {
        console.error("Failed to fetch jobs", e);
      }
    };

    setLoading(true);
    Promise.all([fetchClients(), fetchJobs()]).finally(() => setLoading(false));
  }, []);

  const filteredJobs = useMemo(() => {
    if (!selectedClient) return [];
    const clientName = selectedClient.name || selectedClient.companyName;
    const clientEmail = selectedClient.emails || selectedClient.email;
    return jobRequests.filter((j) => {
      const matchesName = j.customer?.toLowerCase().includes((clientName || "").toLowerCase());
      const matchesEmail = j.customer?.includes(clientEmail) || false;
      return matchesName || matchesEmail;
    });
  }, [jobRequests, selectedClient]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    console.log("Saved data:", formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "#f5f7fb" }}>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h4" fontWeight={700} color="#4180B7" sx={{ mb: 2 }}>
          Client Details
        </Typography>

        {/* Client selector */}
        <Card style={{ padding: 12, borderRadius: 12, marginBottom: 16, marginTop: 50 }}>
          <label style={{ fontWeight: 600, marginRight: 8 }}>Select Client:</label>
          <select
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            style={{ padding: 8, borderRadius: 8, minWidth: 260 }}
          >
            {clients.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name || c.companyName} {c.emails ? `(${c.emails})` : ""}
              </option>
            ))}
          </select>
        </Card>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Overview" />
          <Tab label="Contacts & Preferences" />
          <Tab label="Job Packs" />
          <Tab label="Job Requests" />
          <Tab label="Emails" />
        </Tabs>

        {loading && <Typography>Loading...</Typography>}
        {!loading && selectedClient && (
          <>
            {tab === 0 && (
              <Section title="Overview">
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <Box sx={{ minWidth: 260 }}>
                    <Typography variant="subtitle2">Client</Typography>
                    <Typography variant="h6">{selectedClient.name || selectedClient.companyName}</Typography>
                    <Typography variant="body2" color="text.secondary">{selectedClient.emails}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Cut Off Level</Typography>
                    <Typography>{selectedClient.cutOffLevels || "-"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Default Lab</Typography>
                    <Typography>{selectedClient.laboratoryAddress || "-"}</Typography>
                  </Box>
                </Box>
              </Section>
            )}

            {tab === 1 && (
              <Section title="Contacts & Delivery Preferences">
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2">Primary Email</Typography>
                    <Typography>{selectedClient.emails || "-"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Second Breath Test Required</Typography>
                    <Typography>{selectedClient.secondBreathTestRequired || "-"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Drug Kit Type</Typography>
                    <Typography>{selectedClient.drugKitType || "-"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Non-Negative Samples to Lab</Typography>
                    <Typography>{selectedClient.nonNegativeSamplesToLab || "-"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Laboratory Address</Typography>
                    <Typography>{selectedClient.laboratoryAddress || "-"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Samples Back To Lab (Method)</Typography>
                    <Typography>{selectedClient.sampleDeliveryMethod || "-"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">COC Form</Typography>
                    {/* <input type="checkbox" checked={selectedClient.cocFormCompleted || false} readOnly /> */}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Results Sent</Typography>
                    {/* <input type="checkbox" checked={selectedClient.resultsSent || false} readOnly /> */}
                  </Box>
                </Box>
                <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                  <Button variant="contained" color="primary" onClick={handleEdit}>Edit</Button>
                  <Button variant="outlined" onClick={handleSave}>Save</Button>
                </Box>
                {isEditing && (
                  <Box component="form" sx={{ mt: 2, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2 }}>
                    <TextField
                      label="Primary Email"
                      variant="outlined"
                      name="primaryEmail"
                      value={formData.primaryEmail}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                    <TextField
                      label="Laboratory Address"
                      variant="outlined"
                      name="laboratoryAddress"
                      value={formData.laboratoryAddress}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                    <FormControl variant="outlined" disabled={!isEditing}>
                      <InputLabel>Second Breath Test Required</InputLabel>
                      <Select
                        name="secondBreathTestRequired"
                        value={formData.secondBreathTestRequired}
                        onChange={handleChange}
                        label="Second Breath Test Required"
                      >
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Drug Kit Type"
                      variant="outlined"
                      name="drugKitType"
                      value={formData.drugKitType}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                    <FormControl variant="outlined" disabled={!isEditing}>
                      <InputLabel>Non-Negative Samples to Lab</InputLabel>
                      <Select
                        name="nonNegativeSamplesToLab"
                        value={formData.nonNegativeSamplesToLab}
                        onChange={handleChange}
                        label="Non-Negative Samples to Lab"
                      >
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Samples Back To Lab (Method)"
                      variant="outlined"
                      name="samplesBackToLabMethod"
                      value={formData.samplesBackToLabMethod}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="cocForm"
                          checked={formData.cocForm}
                          onChange={handleChange}
                          disabled={!isEditing}
                        />
                      }
                      label="COC Form"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="resultsSent"
                          checked={formData.resultsSent}
                          onChange={handleChange}
                          disabled={!isEditing}
                        />
                      }
                      label="Results Sent"
                    />
                  </Box>
                )}
              </Section>
            )}

            {tab === 2 && (
              <Section title="Job Packs & Donor Info">
                <Typography variant="body2">Attach links to standard job pack PDFs and donor information sheets here.</Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexDirection: 'row', alignItems: 'center' }}>
                  <Button variant="outlined" component="label" sx={{ minWidth: 180 }}>
                    Upload Job Pack
                    <input type="file" hidden onChange={e => setJobPackFile(e.target.files[0])} />
                  </Button>
                  <Button variant="outlined" component="label" sx={{ minWidth: 180 }}>
                    Upload Donor Info
                    <input type="file" hidden onChange={e => setDonorInfoFile(e.target.files[0])} />
                  </Button>
                </Box>
                <Box sx={{ mt: 2 }}>
                  {jobPackFile && <Typography variant="body2" color="primary">Job Pack: {jobPackFile.name}</Typography>}
                  {donorInfoFile && <Typography variant="body2" color="primary">Donor Info: {donorInfoFile.name}</Typography>}
                </Box>
              </Section>
            )}


            {tab === 3 && (
              <Section title="Job Requests">
                {filteredJobs.length === 0 ? (
                  <Typography>No job requests for this client.</Typography>
                ) : (
                  <Box style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: 8 }}>Ref</th>
                          <th style={{ textAlign: "left", padding: 8 }}>Status</th>
                          <th style={{ textAlign: "left", padding: 8 }}>Location</th>
                          <th style={{ textAlign: "left", padding: 8 }}>Date</th>
                          <th style={{ textAlign: "left", padding: 8 }}>Session Info</th>
                          <th style={{ textAlign: "left", padding: 8 }}>COC Form</th>
                          <th style={{ textAlign: "left", padding: 8 }}>Lab Results</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredJobs.map((j) => (
                          <tr key={j._id}>
                            <td style={{ padding: 8 }}>{j.jobReferenceNo}</td>
                            <td style={{ padding: 8 }}>
                              {j.isCompleted ? "Completed" : j.isAccepted ? "Accepted" : "Pending"}
                            </td>
                            <td style={{ padding: 8 }}>{j.location}</td>
                            <td style={{ padding: 8 }}>{j.dateAndTimeOfCollection ? new Date(j.dateAndTimeOfCollection).toLocaleString() : "-"}</td>
                            <td style={{ padding: 8 }}>{j.sessionInfo || "-"}</td>
                            <td style={{ padding: 8 }}>
                              {j.cocFormUrl ? <a href={j.cocFormUrl} target="_blank" rel="noopener noreferrer">View</a> : "-"}
                            </td>
                            <td style={{ padding: 8 }}>
                              {j.labResultsUrl ? <a href={j.labResultsUrl} target="_blank" rel="noopener noreferrer">View</a> : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                )}
              </Section>
            )}

            {tab === 4 && (
              <Section title="Emails / Notifications">
                <Box>
                  <Typography variant="body2">Show emails sent to collector, client, donor with timestamps and resend actions.</Typography>
                  {/* Example email history */}
                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: 8 }}>Recipient</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Type</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Timestamp</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Replace with actual email data */}
                      <tr>
                        <td style={{ padding: 8 }}>collector@email.com</td>
                        <td style={{ padding: 8 }}>Job Assigned</td>
                        <td style={{ padding: 8 }}>2025-10-02 10:00</td>
                        <td style={{ padding: 8 }}><Button variant="outlined" size="small">Resend</Button></td>
                      </tr>
                      <tr>
                        <td style={{ padding: 8 }}>client@email.com</td>
                        <td style={{ padding: 8 }}>COC Form Sent</td>
                        <td style={{ padding: 8 }}>2025-10-02 10:05</td>
                        <td style={{ padding: 8 }}><Button variant="outlined" size="small">Resend</Button></td>
                      </tr>
                      <tr>
                        <td style={{ padding: 8 }}>donor@email.com</td>
                        <td style={{ padding: 8 }}>Lab Results Sent</td>
                        <td style={{ padding: 8 }}>2025-10-02 10:10</td>
                        <td style={{ padding: 8 }}><Button variant="outlined" size="small">Resend</Button></td>
                      </tr>
                    </tbody>
                  </table>
                </Box>
              </Section>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default ClientHub;


